import { BackgroundJobStatus } from '@erp/shared-types';
import {
  HUBBELL_FEEDBACK_DIVISIONS,
  type HubbellFeedbackDivisionOutcome,
  type HubbellFeedbackRun,
} from '@erp/rpc';

/** How the card shows the org's latest run; pure, so the rules are testable without React. */

/** A Division takes a few seconds; the bar moves nine times over a run. */
export const RUN_POLL_MS = 2_000;
/** Idle, the card still looks for a run a colleague started, so Start is not offered against one. */
export const IDLE_POLL_MS = 30_000;

export function isRunActive(
  run: Pick<HubbellFeedbackRun, 'status'> | null,
): boolean {
  return (
    run !== null &&
    (run.status === BackgroundJobStatus.Queued ||
      run.status === BackgroundJobStatus.Running)
  );
}

/** How often to look for the latest run: often while it goes, rarely otherwise. */
export function latestRunPollMs(
  data: { run: Pick<HubbellFeedbackRun, 'status'> | null } | undefined,
): number {
  return isRunActive(data?.run ?? null) ? RUN_POLL_MS : IDLE_POLL_MS;
}

/** The latest-run query's polling, shared by the Feedback and Mass Renewal cards. */
export const LATEST_RUN_POLLING = {
  refetchInterval: (query: {
    state: { data?: { run: Pick<HubbellFeedbackRun, 'status'> | null } };
  }) => latestRunPollMs(query.state.data),
  refetchOnWindowFocus: true,
} as const;

export interface DivisionLine {
  tab: string;
  /** null for a tab the Division table does not know. */
  division: string | null;
  /** null while the run has not reached this tab. */
  outcome: HubbellFeedbackDivisionOutcome | null;
}

/**
 * One line per Division tab, in the table's order, so a running run shows
 * what is still to come; a tab the run reported that the table does not know
 * follows them. The report, once there, is the complete picture; before it
 * the progress so far.
 */
export function divisionLines(run: HubbellFeedbackRun | null): DivisionLine[] {
  if (!run) return [];
  const outcomes = run.report?.divisions ?? run.progress;
  const byTab = new Map(outcomes.map((outcome) => [outcome.tab, outcome]));
  const known = HUBBELL_FEEDBACK_DIVISIONS.map((division): DivisionLine => ({
    tab: division.letter,
    division: division.name,
    outcome: byTab.get(division.letter) ?? null,
  }));
  const knownTabs = new Set(known.map((line) => line.tab));
  const extra = outcomes
    .filter((outcome) => !knownTabs.has(outcome.tab))
    .map((outcome): DivisionLine => ({
      tab: outcome.tab,
      division: outcome.division,
      outcome,
    }));
  return [...known, ...extra];
}

/** Whether the run's Feedback Files can still be downloaded by the viewer at `now`. */
export function downloadsAvailable(
  run: Pick<
    HubbellFeedbackRun,
    'status' | 'startedByCurrentUser' | 'downloadsExpireAt'
  >,
  now: Date = new Date(),
): boolean {
  return (
    run.status === BackgroundJobStatus.Succeeded &&
    run.startedByCurrentUser &&
    run.downloadsExpireAt !== null &&
    run.downloadsExpireAt.getTime() > now.getTime()
  );
}

export type StartBlocker =
  'not allowed' | 'active run' | 'starting' | 'reading' | 'incomplete drop';

/** Why Start is disabled, in the order the user can do least about; null when it may go. */
export function startBlocker(input: {
  drop: { ready: boolean; reading: boolean };
  run: Pick<HubbellFeedbackRun, 'status'> | null;
  /** The user may start a run (the Pricing create permission). */
  allowed: boolean;
  /** An upload-and-start is already under way from this page. */
  starting: boolean;
}): StartBlocker | null {
  if (!input.allowed) return 'not allowed';
  if (isRunActive(input.run)) return 'active run';
  if (input.starting) return 'starting';
  if (input.drop.reading) return 'reading';
  if (!input.drop.ready) return 'incomplete drop';
  return null;
}
